import styled, { css, keyframes } from 'styled-components';

import { Callout } from '@/nova/components/Callout';
import { Text } from '@/nova/components/Text/Text';
import {
  ANIM,
  BRAND_DEPLOYMENT_ANIM,
  COLORS,
  GRADIENTS,
  MOBILE,
  REDUCED_MOTION,
  SPACE,
  STATUS_COLORS,
} from '@/nova/theme/tokens';
import { CUST } from '../../../../theme/customizer-tokens';
import { cardEnterAt, fadeInWhenReady } from '../../../../theme/mixins';

/* The Video Ads library grid: a "create new" tile plus one card per saved video ad (poster + actions).
   Mirrors the Custom studio's saved-drafts grid so the two libraries read as one system; colours/timings
   come only from theme tokens. On a phone the grid is a list of rows (a small poster beside the ad's details), so a
   screen shows several ads at once; every Marketing library (Image Banners, HTML5 Banners, Mini-games, Landing
   Pages) shares these cards, so all of them list as rows there. */

/** A phone row's poster width (its height follows the poster's 4:5). Layout. */
const ROW_THUMB_W_PX = 72;
/** A phone row's inset around its poster and details (SavedCard's padding, SPACE[8]). Layout. */
const ROW_PAD_PX = 8;
/** A row's narrowest: one column of rows on a phone, two where a tablet has the room. Layout. */
const ROW_MIN_W_PX = 320;
/** The "create new" row's height on a phone: as tall as an ad's row at its shortest (its poster, the row's inset and
 *  its 1px border on both sides). Layout. */
const ROW_NEW_TILE_MIN_H_PX = (ROW_THUMB_W_PX * 5) / 4 + 2 * ROW_PAD_PX + 2;
/** A phone row's action button (edit, download, archive): a full tap target. Layout. */
export const ROW_ACTION_PX = 48;

/** Cards taking the skeleton's place: a short rise from part-visible, all together (no stagger), so the grid is never
 *  empty between the skeleton cards and the cards. */
const cardReveal = keyframes`
  from {
    opacity: 0.5;
    transform: translateY(${SPACE[4]});
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const cardRevealIn = css`
  animation: ${cardReveal} ${ANIM.HUB_CARD_ENTER_MS}ms ${ANIM.EASE} backwards;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

export const Title = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const Body = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
  max-width: 56ch;
`;

/** The "video generation is off" banner (a mature primary art style), between the intro and the grid. */
export const MatureNotice = styled(Callout)`
  margin-bottom: ${SPACE[16]};
`;

export const SavedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;

  ${MOBILE} {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, ${ROW_MIN_W_PX}px), 1fr));
    gap: ${SPACE[8]};
  }
`;

export const NewTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  min-height: 168px;
  border-radius: 14px;
  cursor: pointer;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceFaint};
  border: 1px dashed ${COLORS.hairline};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
    border-color: ${COLORS.hoverBorderLit};
  }

  /* A phone's list: a short row (icon beside its label, a note under them), so the first screen is the ads. */
  ${MOBILE} {
    flex-direction: row;
    flex-wrap: wrap;
    align-content: center;
    column-gap: ${SPACE[8]};
    row-gap: ${SPACE[2]};
    min-height: ${ROW_NEW_TILE_MIN_H_PX}px;
    padding: ${SPACE[8]} ${SPACE[12]};
  }
`;

export const NewTileText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
`;

/** A saved ad's card. Given `$enterIndex` (the library's live grid), it enters in the hubs' stagger as its list
 *  arrives; `backwards` so an archived card keeps its dimmed opacity once in. `$opens`: the whole card opens the ad
 *  (a phone's row, see openRowUnlessControl). */
export const SavedCard = styled.div<{ $archived?: boolean; $enterIndex?: number; $reveal?: boolean; $opens?: boolean }>`
  ${({ $enterIndex }) => ($enterIndex === undefined ? null : cardEnterAt($enterIndex, 'backwards'))}
  ${({ $reveal }) => ($reveal ? cardRevealIn : null)}
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
  opacity: ${({ $archived }) => ($archived ? 0.55 : 1)};
  cursor: ${({ $opens }) => ($opens ? 'pointer' : 'auto')};

  /* A phone's row: the small poster beside the details. */
  ${MOBILE} {
    flex-direction: row;
    align-items: center;
    gap: ${SPACE[12]};
    padding: ${SPACE[8]};
  }
`;

/* The poster tile: the finished ad's OUTPUT video, painted CONTAINED (letterboxed) in a fixed tall frame so
   9:16 and 16:9 both show uncropped and every card is the same height; a play badge sits over it and a click
   opens the ad in the editor. A draft with no render yet shows a neutral placeholder, not a thumbnail. */
export const Poster = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border: 0;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.surfaceInset};

  /* A phone's row: a fixed small poster at the row's start (still 4:5, the art still contained). */
  ${MOBILE} {
    flex: 0 0 ${ROW_THUMB_W_PX}px;
    width: ${ROW_THUMB_W_PX}px;
    border-radius: 8px;
  }
`;

/** The finished ad's output video, contained in the tall poster frame (letterboxed for whichever aspect). The
 *  `#t=0.1` fragment nudges the browser to paint the opening frame as a still poster. */
/** Fades in on its representative frame where it is given `revealOnFrame`. */
export const PosterVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  ${fadeInWhenReady}
`;

/** A draft that has not been rendered yet: a neutral placeholder in place of a (misleading) thumbnail. */
export const PosterEmpty = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};

  /* A phone row's small poster: a smaller line, centred if it wraps. */
  ${MOBILE} {
    padding: 0 ${SPACE[4]};
    font-size: 10px;
    line-height: 1.3;
    text-align: center;
  }
`;

/** A storyboard has been composed but no video rendered yet: a Lumio-accented placeholder distinct from the
 *  neutral "not generated" one, so the three states read apart at a glance. */
export const PosterStoryboard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[8]};
  color: ${COLORS.accent};

  ${MOBILE} {
    gap: ${SPACE[4]};
  }
`;

export const PosterStoryboardText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};

  /* A phone row's small poster: a smaller line, centred if it wraps. */
  ${MOBILE} {
    padding: 0 ${SPACE[4]};
    font-size: 10px;
    line-height: 1.3;
    text-align: center;
  }
`;

/** A glanceable status chip in the poster corner: draft (grey) / storyboard (accent) / video (green). A solid
 *  card chip so it reads over a video thumbnail and an empty placeholder alike. */
export const StatusPill = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.panelBorder};
  pointer-events: none;

  /* A phone row's small poster: the status as its dot alone, so it never covers the art (the poster is a button named
     by its aria-label, so the label was never read out). */
  ${MOBILE} {
    top: ${SPACE[4]};
    left: ${SPACE[4]};
    gap: ${SPACE[0]};
    padding: ${SPACE[4]};
  }
`;

export const StatusDot = styled.span<{ $tone: 'draft' | 'storyboard' | 'video' }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${(p) =>
    p.$tone === 'video' ? STATUS_COLORS.positive : p.$tone === 'storyboard' ? COLORS.accent : COLORS.textFaint};
`;

export const StatusPillText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};

  ${MOBILE} {
    display: none;
  }
`;

export const SavedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: 10px 12px;

  /* A phone's row: the details fill the rest of it (the card pads the row). */
  ${MOBILE} {
    flex: 1 1 auto;
    min-width: 0;
    gap: ${SPACE[4]};
    padding: 0;
  }
`;

/** The ad title on its own full-width row, so a longer name has room to show before it ellipsises. */
export const SavedName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The second row: the format label (9:16 / 16:9) on the left, the action buttons on the right. */
export const SavedFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const SavedMeta = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};

  /* A phone row is narrower beside its actions: two lines at most (a landing page's offer and languages). */
  ${MOBILE} {
    min-width: 0;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
`;

export const SavedActions = styled.div`
  display: flex;
  gap: ${SPACE[4]};
  flex: 0 0 auto;

  /* A phone row's buttons are full tap targets that already keep their icons apart. */
  ${MOBILE} {
    gap: ${SPACE[0]};
  }
`;

export const SavedBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: ${COLORS.textMuted};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }

  /* A phone row's actions (edit, download, archive): tap friendly; the icon keeps its size. */
  ${MOBILE} {
    width: ${ROW_ACTION_PX}px;
    height: ${ROW_ACTION_PX}px;
    border-radius: 12px;
  }
`;

export const EmptyNote = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 13px;
  color: ${COLORS.textFaint};
  padding: 8px 0;
`;

/* ── Header row: title + the "Archived video ads (N)" affordance, vertically aligned ─────────────────── */

export const IntroHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/** Opens the Archived modal; sits to the right of the title, on the same row. */
export const ArchivedLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: ${COLORS.textMuted};
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
    background: ${COLORS.hoverWash};
  }
`;

export const ArchivedLinkText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
`;

/* ── Archived modal: a list of archived ads, each with Restore + (two-step) Permanently delete ───────── */

export const ArchivedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ArchivedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const ArchivedThumb = styled.div`
  flex: 0 0 auto;
  width: 64px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceCard};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ArchivedThumbVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

export const ArchivedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const ArchivedName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ArchivedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
`;

export const ArchivedConfirmText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** Icon-only trash that ARMS the permanent-delete confirm on a row. */
export const ArchivedDeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  cursor: pointer;
  background: transparent;
  color: ${COLORS.textMuted};
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${STATUS_COLORS.negative};
    background: ${COLORS.hoverWash};
  }
`;

/** The confirmed DESTRUCTIVE delete button (there is no shared 'danger' Button variant). */
export const DangerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${STATUS_COLORS.negative};
  cursor: pointer;
  background: ${STATUS_COLORS.negative};
  color: ${COLORS.onAccent};
`;

export const DangerBtnText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
`;

/** "This ad is generating" overlay over the poster, matching the banner card treatment: a dark scrim behind
 *  the drifting Lumio dot-lattice (DotField), with a spark + label above it. */
export const BusyOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  background: ${CUST.logoProcessingScrim};
`;

export const BusyInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  gap: 8px;
`;

export const BusySpark = styled.img`
  width: 22px;
  height: 22px;
`;

/** A generating card's run progress: a thin bar along the bottom of the busy overlay, in the editor's gradient. */
export const CardProgressTrack = styled.div`
  position: absolute;
  left: ${SPACE[12]};
  right: ${SPACE[12]};
  bottom: ${SPACE[12]};
  z-index: 1;
  height: 4px;
  border-radius: 999px;
  background: ${COLORS.surfaceInset};
  overflow: hidden;

  ${MOBILE} {
    left: ${SPACE[8]};
    right: ${SPACE[8]};
    bottom: ${SPACE[8]};
  }
`;

export const CardProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  /* Floored so a run still on its first step shows a visible sliver rather than an empty track. */
  width: ${(p) => Math.max(3, Math.min(100, p.$pct))}%;
  border-radius: inherit;
  background: ${GRADIENTS.deploymentProgress};
  transition: width ${BRAND_DEPLOYMENT_ANIM.PROGRESS_MS}ms ${ANIM.EASE};
`;

export const BusyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textBright};

  /* A phone row's small poster: the Lumio mark and the progress bar say it; the line does not fit. */
  ${MOBILE} {
    display: none;
  }
`;

/** Archive-confirmation sheet: the explanatory line + the right-aligned Cancel / Archive actions. */
export const ConfirmBody = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

export const ConfirmFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
